import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, ArrowRight, Star as StarIcon, Flame, Archive } from 'lucide-react';
import { tools } from '../data/tools';

// Re-export for detail page
export { tools };

// Filter only active tools
const activeTools = tools.filter(t => t.status === 'active');
const allTags = [...new Set(activeTools.flatMap(t => t.tags))];

const ToolDemoPage = () => {
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const filteredTools = selectedTags.length === 0 ? activeTools : activeTools.filter(t => t.tags.some(tag => selectedTags.includes(tag)));

  return (
    <div className="min-h-screen pt-20" data-testid="tool-demo-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent-cyan)] mb-6 font-mono text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--accent-cyan)] bg-[var(--accent-cyan)]/10 flex items-center justify-center">
                <Wrench className="w-8 h-8 text-[var(--accent-cyan)]" />
              </div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--accent-cyan)]">Tool Demos</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Tool Demos</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Explore our comprehensive collection of security tool demonstrations. From web testing to reverse engineering.
            </p>
            {/* <Link to="/services/tool-demos/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-cyan)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-cyan)] transition-colors" data-testid="view-archive-link">
              <Archive className="w-4 h-4" /> View Archived Tools
            </Link> */}
          </div>
        </div>
      </section>

      {activeTools.length === 0 ? (
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="hero-glow top-0 right-1/4" />
          <div className="container-main relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-[var(--surface)] border border-[var(--border)] p-8 md:p-12">
                <div className="space-y-6">
                  <p className="text-[var(--accent-cyan)] text-sm font-mono uppercase tracking-wider">Tool Demonstrations</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)]">Tool Demos Coming Soon</h2>
                  <p className="text-[var(--text-muted)] text-lg leading-relaxed">We're preparing comprehensive demonstrations of security tools covering everything from web testing to reverse engineering. Learn from hands-on walkthroughs of industry-standard tools.</p>
                  <p className="text-[var(--text-muted)] text-base">Check back soon for announcements on the latest tool demonstrations.</p>
                </div>
              </div>
              <div className="bg-[var(--surface-highlight)] border border-[var(--border)] border-dashed p-8 md:p-12 flex flex-col items-center justify-center text-center space-y-4">
                <div className="text-5xl">🔧</div>
                <p className="text-[var(--text-muted)] text-lg">New tools arriving soon</p>
                <p className="text-sm text-[var(--text-muted)]">Follow us for updates on tool demos and security demonstration schedules.</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Filter */}
          <section className="py-8 border-y border-[var(--border)] ">
            <div className="container-main">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <span className="font-mono text-sm text-[var(--accent-cyan)] uppercase tracking-wider">Filter by Tag:</span>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--accent-cyan)] bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent-cyan)]/50'}`} data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}>{tag}</button>
                  ))}
                  {selectedTags.length > 0 && (
                    <button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10 transition-colors duration-200">Clear All</button>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm text-[var(--text-muted)]">Showing {filteredTools.length} of {activeTools.length} active tools</p>
            </div>
          </section>

          {/* Tools Grid */}
          <section className="py-16">
            <div className="container-main">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTools.map((tool) => (
                  <div key={tool.id} className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-cyan)] transition-colors duration-300 overflow-hidden group relative" data-testid={`tool-card-${tool.id}`}>
                    {/* Featured Badge */}
                    <div className="absolute top-4 right-4 flex gap-2 z-10">
                      {tool.featured && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] text-xs font-mono uppercase" data-testid={`featured-badge-${tool.id}`}>
                          <StarIcon className="w-3 h-3 fill-[#FFD700]" /> Featured
                        </span>
                      )}
                      {tool.popular && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-[#FF6B00]/20 border border-[#FF6B00] text-[#FF6B00] text-xs font-mono uppercase" data-testid={`popular-badge-${tool.id}`}>
                          <Flame className="w-3 h-3" /> Popular
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs text-[var(--accent-cyan)] font-mono uppercase">{tool.category}</span>
                        <div className="flex items-center gap-1">
                          <StarIcon className="w-4 h-4 text-[var(--accent-cyan)]" />
                          <span className="text-sm text-[var(--accent-cyan)]">{tool.rating}</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-[var(--text-main)] group-hover:text-[var(--accent-cyan)] transition-colors mb-2">{tool.name}</h3>
                      <p className="text-[var(--text-muted)] text-sm mb-4 line-clamp-2">{tool.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {tool.tags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>
                        ))}
                      </div>
                      <Link to={`/services/tool-demos/${tool.id}`} className="w-full bg-[var(--primary)] text-black font-mono font-bold px-4 py-3 text-sm uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 text-center block" data-testid={`know-more-btn-${tool.id}`}>Know More</Link>
                    </div>
                  </div>
                ))}
              </div>
              {filteredTools.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-[var(--text-muted)] text-lg">No tools match the selected filters.</p>
                  <button onClick={() => setSelectedTags([])} className="mt-4 text-[var(--accent-cyan)] font-mono hover:underline">Clear filters</button>
                </div>
              )}
            </div>
          </section>
        </>
      )}
      <div className="container-main flex justify-center mb-12">
        <Link to="/services/tool-demos/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-purple)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-purple)] transition-colors" data-testid="view-archive-link">
          <Archive className="w-4 h-4" /> View Past Tools
        </Link>
      </div>
    </div>
  );
};

export default ToolDemoPage;
